"use client";

import React from "react";
import {
  DrawerBackContext,
  useDrawerBack,
  type DrawerBackCtx,
} from "./drawerBackContext";

export { useDrawerBack };

const MOBILE_FAB_CLASS = "openings-booking-fab";
const MOBILE_BACKDROP_CLASS = "openings-booking-backdrop";
const MOBILE_DRAWER_CLASS = "openings-booking-drawer";
const MOBILE_DRAWER_OPEN_CLASS = "openings-booking-drawer--open";
const MOBILE_BACKDROP_VISIBLE_CLASS = "openings-booking-backdrop--visible";

/**
 * Self-contained CSS for the FAB, backdrop, and drawer.
 * Injected once per mount; safe to render multiple times (CSS is idempotent).
 */
function MobileBookingDrawerStyles() {
  return (
    <style>{`
      /* ── Mobile FAB ── */
      .${MOBILE_FAB_CLASS} {
        display: none;
      }

      /* ── Mobile Backdrop ── */
      .${MOBILE_BACKDROP_CLASS} {
        position: fixed;
        inset: 0;
        z-index: 9998;
        background: rgba(0, 0, 0, 0.35);
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
      }

      .${MOBILE_BACKDROP_VISIBLE_CLASS} {
        opacity: 1;
        pointer-events: auto;
      }

      /* ── Mobile Drawer ── */
      .${MOBILE_DRAWER_CLASS} {
        position: fixed;
        top: 0;
        right: 0;
        z-index: 9999;
        width: 100vw;
        max-width: 440px;
        height: 100%;
        display: flex;
        flex-direction: column;
        background: var(--openings-booking-skin-surface, #fff);
        box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
        transform: translateX(100%);
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;
        /* Booking-page B&W theme. A booking skin may restyle it through
           the --openings-booking-skin-* vars; everywhere else (every flat
           preset) the pinned B&W values apply. Surface and text must
           defer or a dark skin renders white controls on its canvas. */
        --openings-color-surface: var(--openings-booking-skin-surface, #fff);
        --openings-color-text: var(--openings-booking-skin-text, #111);
        --openings-color-border: var(--openings-booking-skin-control-border, #e5e5e5);
        color: var(--openings-booking-skin-text, #111);
      }

      .${MOBILE_DRAWER_OPEN_CLASS} {
        transform: translateX(0);
      }

      .${MOBILE_DRAWER_CLASS}__header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px 20px;
        background: var(--openings-booking-skin-surface, #fff);
        color: var(--openings-booking-skin-text, #111);
        border-bottom: 1px solid
          var(--openings-booking-skin-divider, #f0f0f0);
        flex-shrink: 0;
      }

      .${MOBILE_DRAWER_CLASS}__title {
        flex: 1;
        font-size: 16px;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .${MOBILE_DRAWER_CLASS}__close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        min-width: 40px;
        height: 40px;
        min-height: 40px;
        border: none;
        border-radius:
          var(
            --openings-booking-control-radius,
            var(--openings-booking-radius-sm, 6px)
          );
        background: var(--openings-color-surface-hover, #f5f5f5);
        color: var(--openings-color-text-secondary, #555);
        cursor: pointer;
        flex-shrink: 0;
        transition: background 0.15s ease;
        -webkit-tap-highlight-color: transparent;
      }

      .${MOBILE_DRAWER_CLASS}__close:hover {
        background: var(--openings-color-border, #e5e5e5);
      }

      .${MOBILE_DRAWER_CLASS}__body {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        -webkit-overflow-scrolling: touch;
      }

      /* Hide the appointment header inside the drawer — its title is
         already shown in the drawer chrome.
         !important needed to override the inline display:flex style. */
      .${MOBILE_DRAWER_CLASS}__body .openings-appointment-header {
        display: none !important;
      }

      .${MOBILE_DRAWER_CLASS}__back {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        min-width: 40px;
        height: 40px;
        min-height: 40px;
        border: none;
        border-radius:
          var(
            --openings-booking-control-radius,
            var(--openings-booking-radius-sm, 6px)
          );
        background: var(--openings-color-surface-hover, #f5f5f5);
        color: var(--openings-color-text-secondary, #555);
        cursor: pointer;
        flex-shrink: 0;
        transition: background 0.15s ease;
        padding: 0;
        -webkit-tap-highlight-color: transparent;
      }

      .${MOBILE_DRAWER_CLASS}__back:hover {
        background: var(--openings-color-border, #e5e5e5);
      }

      .${MOBILE_DRAWER_CLASS}__close:focus-visible,
      .${MOBILE_DRAWER_CLASS}__back:focus-visible {
        outline: 2px solid var(--openings-color-primary, #111);
        outline-offset: 2px;
      }

      @media (max-width: 768px) {
        .${MOBILE_FAB_CLASS} {
          display: inline-flex;
          position: fixed;
          z-index: 9997;
          bottom: 24px;
          right: 24px;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: none;
          border-radius: 50px;
          background: #111;
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          font-family: inherit;
        }

        .${MOBILE_FAB_CLASS}:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        }

        .${MOBILE_FAB_CLASS}:active {
          transform: translateY(0);
        }
      }
    `}</style>
  );
}

export type MobileBookingDrawerProps = {
  /** Label shown on the floating action button. */
  label?: string;
  children: React.ReactNode;
};

export function MobileBookingDrawer({
  label = "Book Now",
  children,
}: MobileBookingDrawerProps) {
  const [open, setOpen] = React.useState(false);
  const [onBack, setOnBack] = React.useState<(() => void) | null>(null);
  const openDrawer = React.useCallback(() => setOpen(true), []);
  const closeDrawer = React.useCallback(() => setOpen(false), []);

  // Lock body scroll while the drawer is open
  React.useEffect(() => {
    if (!open) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [open]);

  // Stable context value
  const ctxValue = React.useMemo<DrawerBackCtx>(
    () => ({
      setDrawerBack: (handler) => setOnBack(() => handler),
    }),
    [],
  );

  return (
    <>
      <MobileBookingDrawerStyles />

      {/* FAB — visible only on mobile via CSS */}
      <button
        type="button"
        className={MOBILE_FAB_CLASS}
        onClick={openDrawer}
        aria-label={label}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="18"
            rx="2"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M16 2v4M8 2v4M3 10h18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        {label}
      </button>

      {/* Backdrop */}
      <div
        className={`${MOBILE_BACKDROP_CLASS}${
          open ? ` ${MOBILE_BACKDROP_VISIBLE_CLASS}` : ""
        }`}
        onClick={closeDrawer}
        role="presentation"
      />

      {/* Drawer */}
      <div
        className={`${MOBILE_DRAWER_CLASS}${
          open ? ` ${MOBILE_DRAWER_OPEN_CLASS}` : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Booking"
      >
        <div className={`${MOBILE_DRAWER_CLASS}__header`}>
          {onBack && (
            <button
              type="button"
              className={`${MOBILE_DRAWER_CLASS}__back`}
              onClick={onBack}
              aria-label="Back"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M15 19l-7-7 7-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          <span className={`${MOBILE_DRAWER_CLASS}__title`}>Appointment</span>
          <button
            type="button"
            className={`${MOBILE_DRAWER_CLASS}__close`}
            onClick={closeDrawer}
            aria-label="Close booking"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className={`${MOBILE_DRAWER_CLASS}__body`}>
          <DrawerBackContext.Provider value={ctxValue}>
            {open ? children : null}
          </DrawerBackContext.Provider>
        </div>
      </div>
    </>
  );
}
